import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { spriteIdOrEmpty } from '../pss-display-utils';
import {
  PssPlayerDetails, PssRoomDesign, PssShipDesign, PssRoom,
} from '../../../services/pss-auth.service';
import { PssSpriteComponent } from '../../pss-sprite/pss-sprite.component';

@Component({
  selector: 'app-rooms-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, PssSpriteComponent],
  templateUrl: './rooms-tab.component.html',
  styleUrls: ['./rooms-tab.component.css'],
})
export class RoomsTabComponent {
  @Input() details: PssPlayerDetails | null = null;
  @Input() roomDesigns = new Map<string, PssRoomDesign>();
  @Input() shipDesigns = new Map<string, PssShipDesign>();
  @Output() roomSelected = new EventEmitter<PssRoom>();

  showShipMap = false;

  cellSize = 25;
  private readonly cellSizeMin = 12;
  private readonly cellSizeMax = 80;
  private readonly cellSizeStep = 5;

  mapDragging = false;
  private mapDragStartX = 0;
  private mapDragStartY = 0;
  private mapDragScrollLeft = 0;
  private mapDragScrollTop = 0;
  private mapDidDrag = false;
  private mapTouchPinchDist = 0;

  roomSearch = '';
  roomSortBy: 'position' | 'name' | 'type' = 'position';
  roomSortAsc = true;
  hideNonCombatRooms = true;

  showDpsCalc = false;
  dpsBaseReload = 4;
  dpsAmmoMod = 100;
  dpsCrewBoost = 0;
  dpsAbPresent = false;
  dpsAbBoost = 0;
  dpsDamage = 100;

  zoomIn(): void  { this.cellSize = Math.min(this.cellSizeMax, this.cellSize + this.cellSizeStep); }
  zoomOut(): void { this.cellSize = Math.max(this.cellSizeMin, this.cellSize - this.cellSizeStep); }

  onMapWheel(e: WheelEvent): void {
    e.preventDefault();
    if (e.deltaY < 0) this.zoomIn(); else this.zoomOut();
  }

  onMapMouseDown(e: MouseEvent): void {
    const el = e.currentTarget as HTMLElement;
    this.mapDragging = true;
    this.mapDidDrag = false;
    this.mapDragStartX = e.pageX - el.offsetLeft;
    this.mapDragStartY = e.pageY - el.offsetTop;
    this.mapDragScrollLeft = el.scrollLeft;
    this.mapDragScrollTop = el.scrollTop;
  }

  onMapMouseMove(e: MouseEvent): void {
    if (!this.mapDragging) return;
    e.preventDefault();
    const el = e.currentTarget as HTMLElement;
    const walkX = (e.pageX - el.offsetLeft) - this.mapDragStartX;
    const walkY = (e.pageY - el.offsetTop) - this.mapDragStartY;
    if (Math.abs(walkX) > 4 || Math.abs(walkY) > 4) this.mapDidDrag = true;
    el.scrollLeft = this.mapDragScrollLeft - walkX;
    el.scrollTop  = this.mapDragScrollTop  - walkY;
  }

  onMapMouseUp(): void    { this.mapDragging = false; }
  onMapMouseLeave(): void { this.mapDragging = false; }

  private touchDist(e: TouchEvent): number {
    const dx = e.touches[0].pageX - e.touches[1].pageX;
    const dy = e.touches[0].pageY - e.touches[1].pageY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  onMapTouchStart(e: TouchEvent): void {
    e.preventDefault();
    const el = e.currentTarget as HTMLElement;
    if (e.touches.length === 1) {
      this.mapDragging = true;
      this.mapDidDrag = false;
      this.mapDragStartX = e.touches[0].pageX - el.offsetLeft;
      this.mapDragStartY = e.touches[0].pageY - el.offsetTop;
      this.mapDragScrollLeft = el.scrollLeft;
      this.mapDragScrollTop  = el.scrollTop;
    } else if (e.touches.length === 2) {
      this.mapDragging = false;
      this.mapTouchPinchDist = this.touchDist(e);
    }
  }

  onMapTouchMove(e: TouchEvent): void {
    e.preventDefault();
    const el = e.currentTarget as HTMLElement;
    if (e.touches.length === 1 && this.mapDragging) {
      const walkX = (e.touches[0].pageX - el.offsetLeft) - this.mapDragStartX;
      const walkY = (e.touches[0].pageY - el.offsetTop) - this.mapDragStartY;
      if (Math.abs(walkX) > 4 || Math.abs(walkY) > 4) this.mapDidDrag = true;
      el.scrollLeft = this.mapDragScrollLeft - walkX;
      el.scrollTop  = this.mapDragScrollTop  - walkY;
    } else if (e.touches.length === 2) {
      const newDist = this.touchDist(e);
      const ratio = newDist / this.mapTouchPinchDist;
      if (ratio > 1.08) { this.zoomIn();  this.mapTouchPinchDist = newDist; }
      else if (ratio < 0.92) { this.zoomOut(); this.mapTouchPinchDist = newDist; }
    }
  }

  onMapTouchEnd(e: TouchEvent): void {
    if (e.touches.length === 0) this.mapDragging = false;
  }

  selectRoomIfNotDrag(r: PssRoom): void {
    if (this.mapDidDrag) { this.mapDidDrag = false; return; }
    this.roomSelected.emit(r);
  }

  get shipGridCols(): number {
    const designCols = parseInt(this.shipDesigns.get(this.details?.shipDesignId ?? '')?.cols ?? '0', 10);
    if (designCols > 0) return designCols;
    if (!this.details) return 24;
    let max = 0;
    for (const r of this.details.rooms.filter(x => x.status === 'Normal')) {
      const c = parseInt(this.roomDesigns.get(r.roomDesignId)?.cols ?? '1', 10);
      if (r.col + c > max) max = r.col + c;
    }
    return max || 24;
  }

  get shipGridRows(): number {
    const designRows = parseInt(this.shipDesigns.get(this.details?.shipDesignId ?? '')?.rows ?? '0', 10);
    if (designRows > 0) return designRows;
    if (!this.details) return 8;
    let max = 0;
    for (const r of this.details.rooms.filter(x => x.status === 'Normal')) {
      const rr = parseInt(this.roomDesigns.get(r.roomDesignId)?.rows ?? '1', 10);
      if (r.row + rr > max) max = r.row + rr;
    }
    return max || 8;
  }

  roomSupportsAI(roomDesignId: string): boolean {
    const flags = this.roomDesigns.get(roomDesignId)?.flags ?? 0;
    return (flags & 1) !== 0;
  }

  private static readonly ROOM_TYPE_STAT = new Set([
    'Laser', 'Cannon', 'AntiCraft', 'Engine', 'Shield', 'Reactor',
    'Research', 'Medical', 'Bridge', 'Command', 'Missile', 'Android',
    'Security', 'Hangar',
  ]);

  roomHasStatBoost(roomDesignId: string): boolean {
    const design = this.roomDesigns.get(roomDesignId);
    if (!design) return false;
    const et = design.enhancementType;
    if (et && et !== 'None' && et !== '') return true;
    return RoomsTabComponent.ROOM_TYPE_STAT.has(design.roomType);
  }

  isCombatRoom(roomDesignId: string): boolean {
    const reloadTime = parseFloat(this.roomDesigns.get(roomDesignId)?.reloadTime ?? '0');
    return reloadTime > 0;
  }

  get deployedRooms(): PssRoom[] {
    const q = this.roomSearch.toLowerCase();
    let list = (this.details?.rooms ?? []).filter(r => r.status === 'Normal');
    if (this.hideNonCombatRooms) list = list.filter(r => this.isCombatRoom(r.roomDesignId));
    if (q) list = list.filter(r =>
      this.roomName2(r.roomDesignId).toLowerCase().includes(q) ||
      this.roomType(r.roomDesignId).toLowerCase().includes(q)
    );
    const dir = this.roomSortAsc ? 1 : -1;
    return list.sort((a, b) => {
      switch (this.roomSortBy) {
        case 'name': return dir * this.roomName2(a.roomDesignId).localeCompare(this.roomName2(b.roomDesignId));
        case 'type': return dir * this.roomType(a.roomDesignId).localeCompare(this.roomType(b.roomDesignId));
        default:     return dir * (a.row !== b.row ? a.row - b.row : a.col - b.col);
      }
    });
  }

  setRoomSort(col: 'position' | 'name' | 'type'): void {
    if (this.roomSortBy === col) { this.roomSortAsc = !this.roomSortAsc; }
    else { this.roomSortBy = col; this.roomSortAsc = true; }
  }

  roomName2(roomDesignId: string): string {
    return this.roomDesigns.get(roomDesignId)?.name ?? `Room#${roomDesignId}`;
  }

  roomType(roomDesignId: string): string {
    return this.roomDesigns.get(roomDesignId)?.roomType ?? '';
  }

  roomGridCol(room: PssRoom): string {
    const cols = parseInt(this.roomDesigns.get(room.roomDesignId)?.cols ?? '1', 10);
    return `${room.col + 1} / span ${cols}`;
  }

  roomGridRow(room: PssRoom): string {
    const rows = parseInt(this.roomDesigns.get(room.roomDesignId)?.rows ?? '1', 10);
    return `${room.row + 1} / span ${rows}`;
  }

  roomSpriteId(roomDesignId: string): string {
    return spriteIdOrEmpty(this.roomDesigns.get(roomDesignId)?.imageSpriteId);
  }

  // TODO: ship hull background renders via CSS background-image of a single URL,
  // which doesn't map cleanly to the new spritesheet-crop model. Disabled for now —
  // room tiles still render correctly atop the grid.
  shipHullSpriteUrl(): string {
    return '';
  }

  get dpsFireRate(): number {
    const ammoFactor = this.dpsAmmoMod * 0.01;
    if (ammoFactor <= 0) return 0;
    const effectiveReload = this.dpsBaseReload / ammoFactor;
    const crewFactor = (this.dpsCrewBoost * 0.01) + 1;
    const abFactor = this.dpsAbPresent
      ? ((((this.dpsAbBoost * 0.01) + 1) * 15) * 0.01) + 1
      : 1;
    const divisor = crewFactor * abFactor;
    if (divisor <= 0) return 0;
    return effectiveReload / divisor;
  }

  get dpsDps(): number {
    const rate = this.dpsFireRate;
    if (rate <= 0) return 0;
    return this.dpsDamage / rate;
  }

  dpsFormat(n: number): string {
    if (!isFinite(n) || isNaN(n)) return '—';
    return n.toFixed(2);
  }
}
